import React, { useState, useEffect, useCallback, useRef } from 'react';
import { schemaSyncApi, databasesApi, syncApi } from '../../services/api';
import DevOnlyGuard from '../../components/common/DevOnlyGuard';
import syncStatusBadge from './syncStatusBadge';
import {
  GitBranch,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  RotateCcw,
  Info,
  Table,
  Columns,
  Key,
  ListFilter, Loader2
} from 'lucide-react';
import StuiSelect from '../../components/common/StuiSelect';
import { formatTimestamp } from '../../utils/dateTime';


const CATEGORY_ICONS = {
  Table: Table,
  Column: Columns,
  Index: ListFilter,
  Constraint: Key
};

const SchemaSyncPage = () => {
  // Setup state
  const [databases, setDatabases] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null); // connStringName of selected DB
  const [environment, setEnvironment] = useState('preprod');
  // Schema Sync routes through SyncEnvironmentResolver as of a20.024, which needs the
  // DataSync config section. A deployed instance has no Environments configured, so it
  // must report unavailable rather than throwing a 500 from deep in resolution.
  const [availability, setAvailability] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(false);

  // Execute state
  const [dryRun, setDryRun] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const pollingRef = useRef(null);

  // Validation state
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedScript, setExpandedScript] = useState(false);
  const [expandedDrift, setExpandedDrift] = useState(false);

  const selectedProjectRef = useRef(selectedProject);
  selectedProjectRef.current = selectedProject;

  // ── Data loading ──────────────────────────────────────────────────
  const loadHistory = useCallback(async (project) => {
    if (!project) return;
    setHistoryLoading(true);
    try {
      const res = await schemaSyncApi.getHistory(project);
      setHistory(res.data || []);
    } catch {
      setHistory([]);
    }
    setHistoryLoading(false);
  }, []);

  useEffect(() => {
    syncApi.getAvailability()
      .then(res => setAvailability(res.data))
      .catch(() => setAvailability({ available: false, reason: 'Unable to reach the sync service.', environments: [], phases: [] }));
  }, []);

  useEffect(() => {
    databasesApi.getAvailable()
      // Fall back to slug — see SyncDashboardPage: app-provisioned DBs have a NULL
      // ConnStringName and were being hidden from this picker entirely.
      .then(res => setDatabases(res.data?.filter(d => d.connStringName || d.slug) || []))
      .catch(() => setDatabases([]));
  }, []);

  useEffect(() => {
    if (selectedProject) loadHistory(selectedProject);
  }, [selectedProject, loadHistory]);

  // A prod acknowledgement covers one database + environment only; any change invalidates it.
  useEffect(() => { setAcknowledged(false); }, [selectedProject, environment]);

  // ── Polling ───────────────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const pollStatus = useCallback(async () => {
    try {
      const res = await schemaSyncApi.getStatus();
      setSyncStatus(res.data);
      if (res.data?.status === 'SUCCESS' || res.data?.status === 'FAILED') {
        stopPolling();
        const proj = selectedProjectRef.current;
        if (proj) loadHistory(proj);
      }
    } catch { /* ignore */ }
  }, [loadHistory, stopPolling]);

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(pollStatus, 1500);
  }, [pollStatus]);

  useEffect(() => { return () => stopPolling(); }, [stopPolling]);

  useEffect(() => {
    pollStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Actions ───────────────────────────────────────────────────────
  const startSync = async () => {
    if (!selectedProject) return;

    if (!canStartSync) return;

    try {
      const res = await schemaSyncApi.start({ Project: selectedProject, DryRun: dryRun, Environment: environment });
      setSyncStatus(res.data);
      startPolling();
      setTimeout(pollStatus, 500);
    } catch (err) {
      const msg = err.response?.data?.errorMessage || err.response?.data?.ErrorMessage || err.message;
      setSyncStatus({ status: 'FAILED', errorMessage: msg, project: selectedProject });
    }
  };

  const acknowledge = () => {
    setSyncStatus(null);
    setExpandedCategories({});
    setExpandedScript(false);
    setExpandedDrift(false);
    setDryRun(false);
    setAcknowledged(false);
  };

  // ── Helpers ───────────────────────────────────────────────────────
  const isRunning = syncStatus?.status === 'RUNNING';
  const isComplete = syncStatus?.status === 'SUCCESS' || syncStatus?.status === 'FAILED';
  const isCompleteForThisProject = isComplete && syncStatus?.project === selectedProject;
  const hasPendingAck = isComplete && syncStatus?.project && syncStatus.project !== selectedProject;
  const showExecuteTile = !!selectedProject;
  // A real run against Prod needs the acknowledgement; a dry run applies no DDL, so it skips it.
  const isProdTarget = environment === 'prod';
  const canStartSync = !isRunning && (!isProdTarget || dryRun || acknowledged);
  const showValidationTile = isCompleteForThisProject;
  const isDryRunResult = syncStatus?.currentStep?.includes('Dry run') || syncStatus?.currentStep?.includes('would be applied');

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '--';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const getResultsByCategory = () => {
    if (!syncStatus?.objectResults) return {};
    return syncStatus.objectResults.reduce((acc, r) => {
      if (!acc[r.category]) acc[r.category] = [];
      acc[r.category].push(r);
      return acc;
    }, {});
  };

  const getCategoryIcon = (cat) => {
    const Icon = CATEGORY_ICONS[cat] || Key;
    return <Icon size={14} />;
  };

  // ── Render ────────────────────────────────────────────────────────
  const resultsByCategory = getResultsByCategory();
  const categoryOrder = ['Table', 'Column', 'Index', 'Constraint'];

  // Block the whole section, not just the tiles. Same treatment as the other dev-only pages.
  if (availability && !availability.available) {
    return <DevOnlyGuard pageName="Schema Sync" message={availability.reason} />;
  }

  return (
    <div className="dev-module sync-dashboard-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <GitBranch size={20} className="stui-imgupload-title-icon" />
            Schema Synchronization
          </h1>
        </div>
      </div>

      <div className="dev-module-content">
        <div className="stui-steps-layout">

          {/* ═══════════ TILE 1: SETUP ═══════════ */}
          <div className="stui-step-panel">
            <div className="stui-step-header">
              <span className="stui-step-number">1</span>
              <h3>Setup</h3>
            </div>
            <p className="stui-step-desc">
              Select a target database and environment. Schema syncs from that
              environment&apos;s <strong>TMPLT</strong> master.
            </p>

            {/* Project selector — dropdown, matching the Data Sync dashboard */}
            {/* Environment first, then database — same order as View Sync. Pick WHERE
                before WHAT; it also puts the prod warning next to its trigger. */}
            <div className="stui-form-field">
              <label className="stui-label-xs stui-mb-xs" htmlFor="schema-env-select">TARGET ENVIRONMENT</label>
              <StuiSelect
                id="schema-env-select"
                className="stui-field-input"
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
                disabled={isRunning}
              >
                <option value="preprod">PreProd</option>
                <option value="prod">Prod</option>
              </StuiSelect>
            </div>

            <div className="stui-form-field">
              <label className="stui-label-xs stui-mb-xs" htmlFor="schema-db-select">TARGET DATABASE</label>
              <StuiSelect
                id="schema-db-select"
                className="stui-field-input"
                value={selectedProject || ''}
                onChange={(e) => { setSelectedProject(e.target.value || null); setExpandedCategories({}); }}
                disabled={isRunning}
              >
                <option value="">— Select database —</option>
                {databases.map(db => {
                  const token = db.connStringName || db.slug;
                  return <option key={token} value={token}>{db.displayName}</option>;
                })}
              </StuiSelect>
            </div>
            {databases.length === 0 && (
              <span className="stui-loading">Loading databases...</span>
            )}

            {environment === 'prod' && (
              <div className="stui-inline-notice stui-inline-notice-warning">
                <AlertTriangle size={13} />
                <span>
                  Targets <strong>PRODUCTION</strong>. A non-dry run applies DDL to
                  {' '}<strong>Prod_{databases.find(d => (d.connStringName || d.slug) === selectedProject)?.displayName || '…'}</strong>.
                  Run a dry run first and read the delta script.
                </span>
              </div>
            )}

            {/* History */}
            {selectedProject && (
              <div>
                <div className="stui-subsection-divider" />
                <button
                  className="stui-btn-ghost"
                  onClick={() => setExpandedHistory(!expandedHistory)}
                >
                  {expandedHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <Clock size={14} />
                  History {historyLoading ? '...' : `(${history.length})`}
                </button>

                {expandedHistory && history.length > 0 && (
                  <div className="stui-mt-xs">
                    <table className="stui-inner-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Objects</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.slice(0, 10).map((h, i) => (
                          <tr key={i}>
                            <td>{(formatTimestamp(h.started_At) || '--')}</td>
                            <td>
                              <span className={syncStatusBadge(h.error_Message === 'DRY RUN' ? 'dryrun' : h.status)}>
                                {h.error_Message === 'DRY RUN' ? 'DRY RUN' : h.status}
                              </span>
                            </td>
                            <td>{h.objects_Applied ?? '--'}</td>
                            <td>{formatDuration(h.duration_Sec)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {expandedHistory && history.length === 0 && !historyLoading && (
                  <div className="stui-text-italic stui-mt-xs">No schema sync history for {selectedProject}.</div>
                )}
              </div>
            )}
          </div>

          {/* ═══════════ TILE 2: EXECUTE ═══════════ */}
          <div className={`stui-step-panel ${isRunning ? 'running' : ''}`}>
            {showExecuteTile && (
              <>
                <div className="stui-step-header">
                  <span className="stui-step-number">2</span>
                  <h3>Execute</h3>
                </div>

                {/* Pending ack for another project */}
                {hasPendingAck && (
                  <div className="stui-inline-notice stui-inline-notice-warning">
                    <AlertTriangle size={14} />
                    <span>
                      <strong>{syncStatus.project}</strong> sync {syncStatus.status === 'SUCCESS' ? 'completed' : 'failed'} —
                      switch back to review, or{' '}
                      <button className="stui-btn-link" onClick={acknowledge}>dismiss</button>
                    </span>
                  </div>
                )}

                {/* Ready state */}
                {!isRunning && !isCompleteForThisProject && (
                  <>
                    <p className="stui-step-desc">
                      Compares <strong>{environment === 'prod' ? 'Prod' : 'PreProd'}_TMPLT</strong> against{' '}
                      <strong>{environment === 'prod' ? 'Prod' : 'PreProd'}_{selectedProject}</strong>, then applies
                      any missing tables, columns, indexes and constraints.
                    </p>

                    <div className="stui-row-between">
                      <label className="stui-checkbox stui-ml-auto-mr">
                        Dry run
                        <input
                          type="checkbox"
                          className="stui-checkbox-input"
                          checked={dryRun}
                          onChange={(e) => setDryRun(e.target.checked)}
                        />
                      </label>
                      <button className="stui-btn stui-btn-primary" onClick={startSync} disabled={!canStartSync}>
                        <Play size={16} />
                        {dryRun ? 'Preview Changes' : 'Sync Schema'}
                      </button>
                    </div>

                    {/* Prod-target acknowledgement — required for a real run, skipped by dry run */}
                    {isProdTarget && (
                      <label className="stui-inline-notice stui-inline-notice-warning">
                        <input
                          type="checkbox"
                          className="stui-checkbox-input"
                          checked={acknowledged}
                          onChange={(e) => setAcknowledged(e.target.checked)}
                        />
                        <span>
                          I understand this applies DDL to{' '}
                          <strong>Prod_{databases.find(d => (d.connStringName || d.slug) === selectedProject)?.displayName || selectedProject}</strong>
                          {' '}in Azure Production.
                        </span>
                      </label>
                    )}

                    <div className="stui-row-8 stui-text-xs stui-c-muted stui-mt-md">
                      <Info size={13} className="stui-shrink-0" />
                      <span>Views are excluded — use <strong>View Synchronization</strong> for views.</span>
                    </div>
                  </>
                )}

                {/* Running state */}
                {isRunning && (
                  <>
                    <div className="stui-row-8 stui-fw-semibold stui-text-primary">
                      <Loader2 size={18} className="stui-spinning" />
                      <span>Syncing {syncStatus.project} schema...</span>
                    </div>

                    {syncStatus.currentStep && (
                      <div className="stui-inline-notice">{syncStatus.currentStep}</div>
                    )}

                    <div className="stui-row-md stui-text-xs stui-text-muted">
                      <span className="stui-row-4"><Clock size={13} /> {formatDuration(syncStatus.elapsedSeconds)}</span>
                    </div>

                    {syncStatus.objectsTotal > 0 && (
                      <div>
                        <div className="stui-progress stui-mb-xs">
                          <div
                            className="stui-progress-bar"
                            style={{ width: `${Math.round((syncStatus.objectsCompleted / syncStatus.objectsTotal) * 100)}%` }}
                          />
                        </div>
                        <span className="stui-text-xs stui-text-muted">
                          {syncStatus.objectsCompleted}/{syncStatus.objectsTotal} objects ({Math.round((syncStatus.objectsCompleted / syncStatus.objectsTotal) * 100)}%)
                        </span>
                      </div>
                    )}
                  </>
                )}

                {/* Complete brief */}
                {isCompleteForThisProject && (
                  <div className={`stui-inline-notice stui-inline-notice-${syncStatus.status === 'SUCCESS' ? 'success' : 'error'}`}>
                    {syncStatus.status === 'SUCCESS' ? (
                      <CheckCircle size={18} className="stui-td-success" />
                    ) : (
                      <XCircle size={18} className="stui-text-error" />
                    )}
                    <div>
                      <div className="stui-fw-semibold">
                        {syncStatus.status === 'SUCCESS' ? (isDryRunResult ? 'Dry Run Complete' : 'Schema Sync Complete') : 'Sync Failed'}
                      </div>
                      <div className="stui-text-xs stui-text-muted">
                        {formatDuration(syncStatus.elapsedSeconds)}
                        {syncStatus.objectsCompleted > 0 && ` • ${syncStatus.objectsCompleted} objects`}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ═══════════ TILE 3: VALIDATION ═══════════ */}
          <div className="stui-step-panel">
            {showValidationTile && (
              <>
                <div className="stui-step-header">
                  <span className="stui-step-number">3</span>
                  <h3>Results</h3>
                </div>

                {/* Overall result */}
                <div className={`stui-inline-notice stui-inline-notice-${syncStatus.status === 'SUCCESS' ? 'success' : 'error'}`}>
                  {syncStatus.status === 'SUCCESS' ? <CheckCircle size={20} /> : <XCircle size={20} />}
                  <div>
                    <div className="stui-fw-semibold">
                      {syncStatus.status !== 'SUCCESS'
                        ? 'Sync failed'
                        : isDryRunResult
                          ? `Dry run — ${syncStatus.objectResults?.length || 0} objects would be applied`
                          : syncStatus.objectResults?.length === 0
                            ? 'Schemas are in sync — no changes needed'
                            : `${syncStatus.objectResults?.length || 0} objects applied`}
                    </div>

                    {syncStatus.errorMessage && (
                      <div className="stui-row-8">
                        <AlertTriangle size={14} />
                        <span>{syncStatus.errorMessage}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Results by category */}
                {syncStatus.objectResults?.length > 0 && (
                  <>
                    {categoryOrder
                      .filter(cat => resultsByCategory[cat]?.length > 0)
                      .map(cat => {
                        const results = resultsByCategory[cat] || [];
                        const failCount = results.filter(r => !r.success).length;
                        const isExpanded = expandedCategories[cat];

                        return (
                          <div key={cat} className="stui-results-panel">
                            <div
                              className="stui-results-panel-header"
                              onClick={() => setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }))}
                            >
                              <span className="stui-flex-1 stui-fw-semibold stui-row-8">
                                {getCategoryIcon(cat)}
                                {cat}s
                              </span>
                              <span className="stui-text-muted">{results.length}</span>
                              <span className="stui-row-4">
                                {failCount === 0 ? (
                                  <CheckCircle size={14} className="stui-td-success" />
                                ) : (
                                  <AlertTriangle size={14} className="stui-text-warning" />
                                )}
                              </span>
                              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </div>

                            {isExpanded && (
                              <table className="stui-inner-table">
                                <tbody>
                                {results.map((r, i) => (
                                  <tr key={i}>
                                    <td>
                                      {r.success
                                        ? <CheckCircle size={14} className="stui-td-success" />
                                        : <XCircle size={14} className="stui-text-error" />}
                                    </td>
                                    <td className="stui-w-full stui-fw-medium">{r.objectName}</td>
                                    <td><span className="stui-badge-gray">{r.action}</span></td>
                                    <td className="stui-text-error stui-text-xs stui-text-nowrap" title={r.error || undefined}>
                                      {r.error && <>⚠ {r.error.substring(0, 60)}{r.error.length > 60 ? '…' : ''}</>}
                                    </td>
                                  </tr>
                                ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        );
                      })}
                  </>
                )}

                {/* Drift warnings */}
                {syncStatus.driftWarnings?.length > 0 && (
                  <>
                    <div className="stui-results-panel">
                      <div
                        className="stui-results-panel-header stui-results-panel-header-warning"
                        onClick={() => setExpandedDrift(v => !v)}
                      >
                        <span className="stui-flex-1 stui-status-text stui-status-text-warning">
                          <AlertTriangle size={14} />
                          Drift Warnings
                        </span>
                        <span className="stui-text-muted">{syncStatus.driftWarnings.length}</span>
                        {expandedDrift ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </div>

                      {expandedDrift && (
                        <table className="stui-inner-table">
                          <tbody>
                          {syncStatus.driftWarnings.map((w, i) => (
                            <tr key={i}>
                              <td>
                                <AlertTriangle size={13} className="stui-text-warning" />
                              </td>
                              <td className="stui-w-full stui-text-xs">{w}</td>
                            </tr>
                          ))}
                          <tr>
                            <td colSpan={2} className="stui-label-note">
                              Drift objects exist in target but not TMPLT. Not auto-removed — review manually.
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      )}
                    </div>
                  </>
                )}

                {/* Delta script (dry run or expandable reference) */}
                {syncStatus.deltaScript && (
                  <>
                    <div className="stui-results-panel">
                      <div
                        className="stui-results-panel-header"
                        onClick={() => setExpandedScript(v => !v)}
                      >
                        <span className="stui-flex-1 stui-fw-semibold">
                          {isDryRunResult ? 'Generated DDL Script' : 'Applied DDL Script'}
                        </span>
                        {expandedScript ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </div>

                      {expandedScript && (
                        <pre className="stui-code-block stui-mt-xs">
                          {syncStatus.deltaScript}
                        </pre>
                      )}
                    </div>
                  </>
                )}

                {/* Acknowledge */}
                <button className="stui-btn stui-btn-primary stui-ml-auto" onClick={acknowledge}>
                  <RotateCcw size={16} />
                  Acknowledge &amp; Reset
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchemaSyncPage;
